// src/activities/wvd/WVDEvent.jsx

import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Counter from './wvd-event-files/Counter';
import RankingsTable from './wvd-event-files/RankingsTable';
import WorldMap from './wvd-event-files/WorldMap';
import Legend from './wvd-event-files/Legend';
import AVIcon from '../../components/icons/AllVoicedIcons';
import { getWVDState } from '../../utils/events/wvdEventUtils';

function WVDEvent() {
  const { t } = useTranslation();
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

  const { phase, eventStart, eventEnd, nextEventStart } = useMemo(
    () => getWVDState(),
    []
  );

  const [currentTime, setCurrentTime] = useState(new Date());
  const [assessmentCount, setAssessmentCount] = useState(0);
  const [countryData, setCountryData] = useState([]);

  const ctaLabel = {
    before: t('get_ready_event', 'Get ready for the event'),
    active: t('participate_now', 'Participate now'),
    after: t('see_results', 'See results'),
  }[phase];

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    let interval;

    const pollAssessments = async () => {
      try {
        const response = await fetch(`${apiUrl}/voice-assessments/wvd-count`);
        const data = await response.json();
        setAssessmentCount(data.total);
        setCountryData(data.byCountry);
      } catch (error) {
        console.error('Error fetching WVD data:', error);
      }
    };

    pollAssessments();

    if (phase === 'active') {
      interval = setInterval(pollAssessments, 5000);
    }

    return () => interval && clearInterval(interval);
  }, [phase, apiUrl]);

  // 👉 CTA únic: sempre anar a l'app
  const handleCTAClick = () => {
    window.open('/app', '_blank');
  };

  return (
    <div className="wvd-event">

      {/* HERO */}
      <section className="wvd-event__hero">
        <div className="wvd-event__hero-logos">
          <img src="/images/wvd/wvd.png" alt="World Voice Day logo" />
          <img src="/images/logo/other-logos/wvd-event-logo.png" alt="WVD Marathon" />
        </div>

        <h1 className="wvd-event__title">
          {t('global_perceptual_voice_evaluation_marathon')}
        </h1>

        <div className="wvd-event__intro">
          <p>
            Join the <strong>Global Perceptual Voice Evaluation Marathon</strong> — now in its second edition.
          </p>
          <p>
            Train your listening skills while contributing to global voice science.
          </p>
          <p>
            Compare your judgments with expert evaluations and be part of a shared dataset.
          </p>
          <p className="wvd-event__tagline">
            <strong>Learn. Compare. Contribute.</strong>
          </p>
        </div>
      </section>

      {/* COUNTER */}
      <section className="wvd-event__counter">
        <Counter 
          currentTime={currentTime} 
          eventStart={eventStart} 
          eventEnd={eventEnd} 
          nextEventStart={nextEventStart} 
        />
      </section>

      {/* CARDS */}
      <section className="wvd-event__cards">
        <div className="wvd-card">
          <AVIcon icon="learn" size={28} color="#7a1f3d" />
          <h3>Learn</h3>
          <p>Train your auditory-perceptual listening skills.</p>
        </div>

        <div className="wvd-card">
          <AVIcon icon="analytics" size={28} color="#7a1f3d" />
          <h3>Compare</h3>
          <p>Compare your judgments with expert evaluations.</p>
        </div>

        <div className="wvd-card">
          <AVIcon icon="globe" size={28} color="#7a1f3d" />
          <h3>Contribute</h3>
          <p>Contribute to a global voice research dataset.</p>
        </div>
      </section>

      {/* CTA */}
      <section className="wvd-event__cta">
      <button 
        className="wvd-event__button" 
        onClick={handleCTAClick}
      >
        {ctaLabel}
      </button>

        <p className="wvd-event__cta-note">
          {t(
            'wvd_cta_note',
            'Create your account, complete your profile, and be ready to participate.'
          )}
        </p>
      </section>

      {/* DATA */}
      <section 
        className="wvd-event__data"
        style={{
          opacity: phase === 'active' ? 1 : 0.5,
          filter: phase === 'active' ? 'none' : 'grayscale(60%)'
        }}
      >
        <h2>{t('evaluation_count', 'Evaluation Count')}</h2>

        <div className="wvd-event__count">
          {assessmentCount}
        </div>

        <h2>{t('country_ranking')}</h2>
        <RankingsTable data={countryData} />

        <h2>{t('evaluation_map', 'Evaluation Map')}</h2>
        <WorldMap data={countryData} />
        <Legend />
      </section>

    </div>
  );
}

export default WVDEvent;
